import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  TrendingUp, TrendingDown, Award, Vote, Users as UsersIcon, CheckCircle,
  XCircle, MinusCircle, Calendar, Zap, Shuffle, Network, GraduationCap,
} from "lucide-react";
import { useState } from "react";
import { useLegislatorHistory } from "@/components/profile/history";
import { CosponsorshipCard } from "@/components/profile/CosponsorshipCard";
import { PolicyFocus } from "@/components/profile/PolicyFocus";
import { LegislativePortfolio } from "@/components/profile/LegislativePortfolio";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, LineChart,
  Line, XAxis, YAxis, ReferenceLine, CartesianGrid, BarChart, Bar,
} from "recharts";
import { ordinal, statusPillClass, policyAreaLabel } from "./shared";

/* eslint-disable no-unused-vars */
const CURRENT_CONGRESS = 119;

export const LegislationTab = ({
  legislatorId,
  legislator,
  navigate,
  saveScroll,
  sponsoredBills,
  statsData: careerStatsData,
  votingData,
  handlePieClick
}) => {
  const history = useLegislatorHistory(legislatorId);
  const [scope, setScope] = useState("current");
  const [focusArea, setFocusArea] = useState(null);
  const cur = (history?.congresses || []).find((c) => c.congress === CURRENT_CONGRESS);
  const st = legislator?.stats || {};
  const curSponsored = cur?.sponsored ?? st.billsSponsoredSubstantiveCurrent ?? 0;
  const currentBills = (sponsoredBills || []).filter((b) => !b.congress || Number(b.congress) === CURRENT_CONGRESS);
  const curEnacted = cur?.enacted ?? st.billsLawCurrent ?? 0;
  const statsData = scope === "career" ? careerStatsData : [
    { name: "Sponsored", value: curSponsored, fill: "#3b82f6" },
    { name: "Enacted", value: curEnacted, fill: "#22c55e" },
    { name: "Pending", value: Math.max(0, curSponsored - curEnacted), fill: "#f59e0b" },
  ];
  const attendance = (history?.congresses || []).filter((c) => c.participation != null).map((c) => ({ congress: `${c.congress}th`, rate: c.participation }));
  const topicSource = scope === "career" ? (history?.careerPolicyAreas || {}) : (cur?.policyAreas || {});
  const voteAreas = history?.votePolicyAreas || {};
  const topics = Object.entries(topicSource).map(([area, sponsored]) => ({ area, sponsored, votes: scope === "current" ? (voteAreas[area] || 0) : 0 }));
  Object.entries(scope === "current" ? voteAreas : {}).forEach(([area, votes]) => { if (!topics.find((t) => t.area === area)) topics.push({ area, sponsored: 0, votes }); });
  topics.sort((a, b) => (b.sponsored + b.votes) - (a.sponsored + a.votes));
  const topTopics = topics.slice(0, 10).map((t) => ({ ...t, label: policyAreaLabel(t.area) }));

  const ScopeToggle = ({ testId }) => (
    <div className="inline-flex rounded-lg border border-gray-300 bg-gray-100 p-0.5 text-xs font-semibold" data-testid={testId}>
      {[["current", "119th Congress"], ["career", "Career"]].map(([id, label]) => (
        <button key={id} type="button" onClick={() => setScope(id)} data-testid={`${testId}-${id}`}
          className={`px-3 py-1 rounded-md transition-colors ${scope === id ? "bg-white text-gray-900 shadow-sm" : "text-gray-600 hover:text-gray-900"}`}>{label}</button>
      ))}
    </div>
  );

  return (
        <div className="space-y-8" data-testid="profile-tab-panel-legislation">
            {/* Performance Charts */}
            <Card className="p-6 bg-white rounded-xl border-2 border-gray-200">
              <div className="flex items-start justify-between gap-3 mb-6">
                <h2 className="text-2xl font-bold text-gray-900" title="Sponsorship outcomes and attendance, switchable between the current Congress and full career.">Performance Metrics</h2>
                <ScopeToggle testId="legislation-scope" />
              </div>
              <p className="text-sm text-gray-600 mb-4">Click on any chart section for detailed information. {scope === "current" ? "Showing the 119th Congress." : "Showing full career totals."}</p>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center" title="How the member's sponsored bills split between enacted into law and still pending. Click a slice for the bill list.">Legislative Activity</h3>
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart margin={{ top: 20, right: 40, bottom: 20, left: 40 }}>
                      <Pie
                        data={statsData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => (percent * 100 >= 8 ? `${name} ${(percent * 100).toFixed(0)}%` : "")}
                        outerRadius={72}
                        dataKey="value"
                        onClick={(data) => handlePieClick(data, "Legislative Activity")}
                        style={{ cursor: 'pointer' }}
                      >
                        {statsData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value, name) => [`${value}`, name]} />
                      <Legend verticalAlign="bottom" height={36} iconSize={10} wrapperStyle={{ fontSize: "12px" }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center" title="Share of roll-call votes cast per Congress, from Voteview vote counts. Trends beat a single-session snapshot.">Attendance Over Career</h3>
                  {attendance.length >= 2 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <LineChart data={attendance} margin={{ top: 20, right: 30, bottom: 20, left: 0 }} data-testid="attendance-line">
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="congress" tick={{ fontSize: 11 }} />
                        <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
                        <Tooltip formatter={(v) => [`${v}%`, "Participation"]} />
                        <Line type="monotone" dataKey="rate" stroke="#22c55e" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart margin={{ top: 20, right: 40, bottom: 20, left: 40 }}>
                        <Pie data={votingData} cx="50%" cy="50%" labelLine={false} outerRadius={72} dataKey="value"
                          label={({ name, percent }) => (percent * 100 >= 8 ? `${name} ${(percent * 100).toFixed(0)}%` : "")}
                          onClick={(data) => handlePieClick(data, "Attendance Record")} style={{ cursor: 'pointer' }}>
                          {votingData.map((entry, index) => (<Cell key={`cell-${index}`} fill={entry.fill} />))}
                        </Pie>
                        <Tooltip formatter={(value, name) => [`${value}`, name]} />
                        <Legend verticalAlign="bottom" height={36} iconSize={10} wrapperStyle={{ fontSize: "12px" }} />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>
            </Card>

            {/* Policy Focus (portfolio vs chamber) */}
            <PolicyFocus bills={currentBills} chamber={legislator?.chamber} selectedArea={focusArea} onSelectArea={setFocusArea} ScopeToggle={null} />

            {/* Co-Sponsorship Activity */}
            <CosponsorshipCard legislatorId={legislatorId} legislator={legislator} />

            <LegislativePortfolio bills={currentBills} filterArea={focusArea} onClearFilter={() => setFocusArea(null)} saveScroll={saveScroll} />
        </div>
  );
};
